import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { dbClient } from '@/services/db-client';
import { cn } from '@/lib/utils';
import { ACTIVATION_STEPS } from './commCatalog';
import { DestinationField } from './DestinationField';
import { formatUnknownError } from './commUi';

export type FlowTemplate = {
    key: string;
    channel: string;
    locale: string;
    variant: string;
    subject: string | null;
    title: string | null;
    body: string;
    preheader: string | null;
    click_url: string | null;
    image_source: string | null;
    required_data?: string[] | null;
    fallback_template_key?: string | null;
    is_active: boolean;
};

type Piece = {
    key: string;
    channel: string;
    caption: string;
    showSubject: boolean;
    showClick: boolean;
};

type Choice = { id: string; label: string; pieces: Piece[] };

type FlowStep =
    | { kind: 'note'; text: string }
    | { kind: 'branch'; title: string; choices: Choice[] };

const FLOWS: Record<(typeof ACTIVATION_STEPS)[number]['id'], FlowStep[]> = {
    a0: [
        { kind: 'note', text: 'La persona se registra' },
        { kind: 'note', text: 'Pasan unas 2 horas' },
        {
            kind: 'branch',
            title: 'Según cómo se definió',
            choices: [
                {
                    id: 'principiante',
                    label: 'Principiante',
                    pieces: [
                        { key: 'activacion_a0.subject.principiante', channel: 'subject', caption: 'Asunto', showSubject: true, showClick: false },
                        { key: 'activacion_a0.peticion.principiante', channel: 'fragment', caption: 'Qué le pedimos', showSubject: false, showClick: false },
                    ],
                },
                {
                    id: 'intermedio',
                    label: 'Intermedio',
                    pieces: [
                        { key: 'activacion_a0.subject.intermedio', channel: 'subject', caption: 'Asunto', showSubject: true, showClick: false },
                        { key: 'activacion_a0.peticion.intermedio', channel: 'fragment', caption: 'Qué le pedimos', showSubject: false, showClick: false },
                    ],
                },
                {
                    id: 'experto',
                    label: 'Experto',
                    pieces: [
                        { key: 'activacion_a0.subject.experto', channel: 'subject', caption: 'Asunto', showSubject: true, showClick: false },
                        { key: 'activacion_a0.peticion.experto', channel: 'fragment', caption: 'Qué le pedimos', showSubject: false, showClick: false },
                    ],
                },
            ],
        },
        {
            kind: 'branch',
            title: 'Según el agua',
            choices: [
                { id: 'dulce', label: 'Dulce', pieces: [{ key: 'activacion_a0.contenido.dulce', channel: 'fragment', caption: 'Contenido', showSubject: false, showClick: false }] },
                { id: 'salada', label: 'Salada', pieces: [{ key: 'activacion_a0.contenido.salada', channel: 'fragment', caption: 'Contenido', showSubject: false, showClick: false }] },
                { id: 'ambas', label: 'Ambas', pieces: [{ key: 'activacion_a0.contenido.ambas', channel: 'fragment', caption: 'Contenido', showSubject: false, showClick: false }] },
            ],
        },
        {
            kind: 'branch',
            title: 'El texto que reciben todos',
            choices: [
                { id: 'cuerpo', label: 'Cuerpo y botón', pieces: [{ key: 'activacion_a0', channel: 'email', caption: 'Cuerpo', showSubject: false, showClick: true }] },
            ],
        },
    ],
    a1: [
        { kind: 'note', text: 'Lleva unas 48 horas registrada' },
        { kind: 'note', text: 'Todavía no ha subido una captura' },
        {
            kind: 'branch',
            title: 'El correo',
            choices: [
                { id: 'a1', label: 'Mensaje', pieces: [{ key: 'activacion_a1', channel: 'email', caption: 'Mensaje', showSubject: true, showClick: true }] },
            ],
        },
    ],
    a2: [
        { kind: 'note', text: 'Lleva unos 4 días' },
        {
            kind: 'branch',
            title: 'Qué sabemos de su zona',
            choices: [
                { id: 'sin', label: 'Sin zona', pieces: [{ key: 'activacion_a2_sin_zona', channel: 'email', caption: 'Mensaje', showSubject: true, showClick: true }] },
                {
                    id: 'abs',
                    label: 'Con zona, pocas capturas',
                    pieces: [
                        { key: 'activacion_a2_zona_abs', channel: 'email', caption: 'Mensaje', showSubject: true, showClick: true },
                        { key: 'activacion_a2.tecnica', channel: 'fragment', caption: 'Técnica que se mete en el texto', showSubject: false, showClick: false },
                    ],
                },
                {
                    id: 'pct',
                    label: 'Con zona, muchas capturas',
                    pieces: [
                        { key: 'activacion_a2_zona_pct', channel: 'email', caption: 'Mensaje', showSubject: true, showClick: true },
                        { key: 'activacion_a2.tecnica', channel: 'fragment', caption: 'Técnica que se mete en el texto', showSubject: false, showClick: false },
                    ],
                },
                { id: 'atlas', label: 'Sin datos, Atlas', pieces: [{ key: 'activacion_a2_fallback_atlas', channel: 'email', caption: 'Mensaje', showSubject: true, showClick: true }] },
                { id: 'academia', label: 'Sin datos, Academia', pieces: [{ key: 'activacion_a2_fallback_academia', channel: 'email', caption: 'Mensaje', showSubject: true, showClick: true }] },
            ],
        },
    ],
    a3: [
        { kind: 'note', text: 'Lleva unos 7 días' },
        {
            kind: 'branch',
            title: 'Una sola rama',
            choices: [
                { id: 'captura', label: 'Ya tiene captura', pieces: [{ key: 'activacion_a3_captura', channel: 'email', caption: 'Mensaje', showSubject: true, showClick: true }] },
                { id: 'seguidos', label: 'Sigue a gente', pieces: [{ key: 'activacion_a3_seguidos', channel: 'email', caption: 'Mensaje', showSubject: true, showClick: true }] },
            ],
        },
        { kind: 'note', text: 'Si ya activó la cuenta, no se envía' },
    ],
};

type PieceDraft = Piece & {
    locale: string;
    variant: string;
    subject: string;
    preheader: string;
    body: string;
    clickUrl: string;
    imageSource: string | null;
    requiredData: string[] | null;
    fallbackKey: string | null;
    isActive: boolean;
    found: boolean;
};

function draftsFor(choice: Choice, templates: FlowTemplate[]): PieceDraft[] {
    return choice.pieces.map((piece) => {
        const row = templates.find((template) => template.key === piece.key && template.channel === piece.channel);
        return {
            ...piece,
            locale: row?.locale || 'es',
            variant: row?.variant || 'A',
            subject: row?.subject || row?.title || '',
            preheader: row?.preheader || '',
            body: row?.body || '',
            clickUrl: row?.click_url || '',
            imageSource: row?.image_source ?? null,
            requiredData: row?.required_data ?? null,
            fallbackKey: row?.fallback_template_key ?? null,
            isActive: row?.is_active ?? true,
            found: Boolean(row),
        };
    });
}

export function RegistrationFlowDialog(props: {
    stepId: (typeof ACTIVATION_STEPS)[number]['id'];
    templates: FlowTemplate[];
    onClose: () => void;
}) {
    const step = ACTIVATION_STEPS.find((item) => item.id === props.stepId)!;
    const flow = FLOWS[props.stepId];
    const queryClient = useQueryClient();
    const initialSelection = () => {
        const picked: Record<string, string> = {};
        for (const item of flow) {
            if (item.kind === 'branch' && item.choices[0]) picked[item.title] = item.choices[0].id;
        }
        return picked;
    };
    const piecesFor = (picked: Record<string, string>) => {
        const drafts: PieceDraft[] = [];
        for (const item of flow) {
            if (item.kind !== 'branch') continue;
            const choice = item.choices.find((entry) => entry.id === picked[item.title]) ?? item.choices[0];
            if (choice) drafts.push(...draftsFor(choice, props.templates));
        }
        return drafts;
    };
    const [selected, setSelected] = useState<Record<string, string>>(initialSelection);
    const [pieces, setPieces] = useState<PieceDraft[]>(() => piecesFor(initialSelection()));

    const save = useMutation({
        mutationFn: async (drafts: PieceDraft[]) => {
            for (const draft of drafts) {
                if (!draft.found) throw new Error('Ese texto no está en la base');
                const subject = draft.subject.trim() || null;
                const body = draft.channel === 'subject' ? draft.subject : draft.body;
                const { error } = await dbClient.rpc('admin_update_comm_template', {
                    p_key: draft.key,
                    p_channel: draft.channel,
                    p_locale: draft.locale,
                    p_variant: draft.variant,
                    p_body: body,
                    p_subject: subject,
                    p_title: subject,
                    p_preheader: draft.preheader.trim() || null,
                    p_click_url: draft.clickUrl.trim() || null,
                    p_image_source: draft.imageSource,
                    p_required_data: draft.requiredData,
                    p_fallback_template_key: draft.fallbackKey,
                    p_is_active: draft.isActive,
                });
                if (error) throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'comm-templates'] });
        },
    });

    const pick = (title: string, choice: Choice) => {
        const next = { ...selected, [title]: choice.id };
        setSelected(next);
        setPieces(piecesFor(next));
        save.reset();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={props.onClose}>
            <div
                role="dialog"
                aria-label={step.title}
                className="grid max-h-[90vh] w-full max-w-5xl grid-cols-1 gap-6 overflow-y-auto rounded-xl border border-border bg-background p-6 lg:grid-cols-2"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="space-y-3">
                    <div>
                        <h2 className="text-lg font-semibold">{step.title}</h2>
                        <p className="text-sm text-muted-foreground">
                            {step.when}. Lo enviaba {step.from}. El texto se conserva; ya no sale.
                        </p>
                    </div>
                    {flow.map((item, index) => {
                        if (item.kind === 'note') {
                            return (
                                <div key={`${item.text}-${index}`} className="flex flex-col items-start gap-2">
                                    {index > 0 && <span className="pl-6 text-muted-foreground">↓</span>}
                                    <p className="rounded-full border border-border px-3 py-1 text-sm text-muted-foreground">{item.text}</p>
                                </div>
                            );
                        }
                        return (
                            <div key={item.title} className="space-y-2">
                                <span className="pl-6 text-muted-foreground">↓</span>
                                <p className="text-sm font-medium">{item.title}</p>
                                <div className="flex flex-wrap gap-2 pl-4">
                                    {item.choices.length === 1 ? (
                                        <p className="rounded-lg border border-border bg-card px-3 py-2 text-sm">{item.choices[0].label}</p>
                                    ) : (
                                        item.choices.map((choice) => (
                                            <button
                                                key={choice.id}
                                                type="button"
                                                className={cn(
                                                    'rounded-lg border px-3 py-2 text-sm',
                                                    choice.id === selected[item.title]
                                                        ? 'border-primary bg-primary text-primary-foreground'
                                                        : 'border-border bg-card hover:border-primary'
                                                )}
                                                onClick={() => pick(item.title, choice)}
                                            >
                                                {choice.label}
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="space-y-4">
                    {pieces.map((piece, index) => (
                        <div key={piece.key} className="space-y-2">
                            <p className="text-sm font-medium">{piece.caption}</p>
                            {!piece.found && <p className="text-sm text-destructive">No está en la base.</p>}
                            {piece.showSubject && (
                                <>
                                    <label className="block text-sm">
                                        Asunto
                                        <input
                                            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                                            value={piece.subject}
                                            onChange={(event) =>
                                                setPieces((current) =>
                                                    current.map((item, itemIndex) =>
                                                        itemIndex === index ? { ...item, subject: event.target.value } : item
                                                    )
                                                )
                                            }
                                        />
                                    </label>
                                    <label className="block text-sm">
                                        Línea bajo el asunto
                                        <input
                                            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                                            value={piece.preheader}
                                            onChange={(event) =>
                                                setPieces((current) =>
                                                    current.map((item, itemIndex) =>
                                                        itemIndex === index ? { ...item, preheader: event.target.value } : item
                                                    )
                                                )
                                            }
                                        />
                                    </label>
                                </>
                            )}
                            {piece.channel !== 'subject' && (
                            <label className="block text-sm">
                                Texto
                                <textarea
                                    className="mt-1 min-h-[160px] w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                                    value={piece.body}
                                    onChange={(event) =>
                                        setPieces((current) =>
                                            current.map((item, itemIndex) =>
                                                itemIndex === index ? { ...item, body: event.target.value } : item
                                            )
                                        )
                                    }
                                />
                            </label>
                            )}
                            {piece.showClick && (
                                <DestinationField
                                    label="Destino del botón"
                                    value={piece.clickUrl}
                                    onChange={(clickUrl) =>
                                        setPieces((current) =>
                                            current.map((item, itemIndex) =>
                                                itemIndex === index ? { ...item, clickUrl } : item
                                            )
                                        )
                                    }
                                />
                            )}
                        </div>
                    ))}
                    {save.isError && <p className="text-sm text-destructive">{formatUnknownError(save.error, 'No se pudo guardar')}</p>}
                    {save.isSuccess && <p className="text-sm text-primary">Guardado.</p>}
                    <div className="flex gap-2">
                        <button
                            type="button"
                            className="rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-60"
                            disabled={save.isPending || pieces.some((piece) => !piece.found)}
                            onClick={() => save.mutate(pieces)}
                        >
                            {save.isPending ? 'Guardando…' : 'Guardar este texto'}
                        </button>
                        <button type="button" className="rounded-lg border border-border px-3 py-2 text-sm" onClick={props.onClose}>
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
